import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Grid,
    Stack,
    Typography,
    TextField,
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Switch,
    FormControlLabel,
    Divider,
    Chip,
    useTheme,
    IconButton,
    Tooltip,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { DataGrid } from "@mui/x-data-grid";

import ptBR from "date-fns/locale/pt-BR";
import * as XLSX from "xlsx";

import { djangoApi } from "../../utils/axios/axios.utils";
import { tokens } from "../../theme";

import { subDays, startOfDay, endOfDay } from "date-fns";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";

import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip as ReTooltip,
    Legend,
} from "recharts";

// helpers
const toISO = (d) => (d ? new Date(d).toISOString().slice(0, 10) : null);

const fmtBRL = (v) =>
    typeof v === "number"
        ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        : Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const uniqueSorted = (arr) =>
    [...new Set(arr.filter(Boolean))].sort((a, b) =>
        String(a).localeCompare(String(b), "pt-BR", { numeric: true })
    );

const TOP_N_COLORS = 8;

const PIE_COLORS_FALLBACK = [
    "#66bb6a",
    "#42a5f5",
    "#ffca28",
    "#ffb74d",
    "#29b6f6",
    "#d4e157",
    "#7e57c2",
    "#bdbdbd",
    "#26a69a",
    "#5c6bc0",
];

function buildColorSalesData(sourceRows, qtyField, totalField) {
    const map = new Map();

    for (const r of sourceRows) {
        const color = r.product_id_produto_color || "Sem cor";
        const qty = Number(r[qtyField] ?? 0);
        const total = Number(r[totalField] ?? 0);

        if (!map.has(color)) {
            map.set(color, { color, qty: 0, total: 0 });
        }

        const ref = map.get(color);
        ref.qty += qty;
        ref.total += total;
    }

    return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

function buildPieData(colorSalesData) {
    const base = [...colorSalesData];
    const top = base.slice(0, TOP_N_COLORS);
    const rest = base.slice(TOP_N_COLORS);

    const restAgg = rest.reduce(
        (acc, item) => ({
            qty: acc.qty + Number(item.qty || 0),
            total: acc.total + Number(item.total || 0),
        }),
        { qty: 0, total: 0 }
    );

    const byQty = top.map((item) => ({
        name: item.color || "—",
        value: Number(item.qty || 0),
    }));

    const byVal = top.map((item) => ({
        name: item.color || "—",
        value: Number(item.total || 0),
    }));

    if (rest.length) {
        byQty.push({ name: "Outras", value: Number(restAgg.qty || 0) });
        byVal.push({ name: "Outras", value: Number(restAgg.total || 0) });
    }

    return { byQty, byVal };
}

function ColorPieCard({
    title,
    data,
    total,
    pieColors,
    isCurrency = false,
    colors,
}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                bgcolor: colors.pink[700],
                height: "100%",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
            }}
            elevation={8}
        >
            <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ flexShrink: 0 }}>
                {title}
            </Typography>

            <Box sx={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 0, right: 30, bottom: 10, left: 8 }}>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={38}
                            outerRadius={82}
                            paddingAngle={2}
                            minAngle={8}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                            labelLine
                        >
                            {data.map((_, idx) => (
                                <Cell key={`${title}-${idx}`} fill={pieColors[idx % pieColors.length]} />
                            ))}
                        </Pie>

                        <ReTooltip
                            formatter={(value, name, ctx) => {
                                const pct = total > 0 ? (Number(value) / total) * 100 : 0;
                                return [
                                    isCurrency
                                        ? `${fmtBRL(value)} (${pct.toFixed(1)}%)`
                                        : `${value} (${pct.toFixed(1)}%)`,
                                    ctx?.payload?.name || name,
                                ];
                            }}
                        />

                        <Legend
                            layout="vertical"
                            align="right"
                            verticalAlign="middle"
                            wrapperStyle={{ right: 0, paddingLeft: 8, fontSize: 12 }}
                            formatter={(value, entry) => {
                                const pct = total > 0
                                    ? ((Number(entry.payload.value || 0) / total) * 100).toFixed(1)
                                    : "0.0";
                                return `${value} — ${pct}%`;
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );
}

function HighlightCard({ title, value, colors, bg }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.2,
                bgcolor: bg,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
            }}
        >
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                {title}
            </Typography>
            <Typography variant="body2" fontWeight={800} textAlign="right">
                {value}
            </Typography>
        </Paper>
    );
}

export default function SoldProdsDashboard() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    useEffect(() => {
        if (!startDate && !endDate) {
            const today = new Date();
            const ninetyDaysAgo = subDays(today, 90);
            setStartDate(startOfDay(ninetyDaysAgo));
            setEndDate(endOfDay(today));
        }
    }, [startDate, endDate]);

    const [rowsRaw, setRowsRaw] = useState([]); // vendidos
    const [rowsAllRaw, setRowsAllRaw] = useState([]); // todos
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const [seller, setSeller] = useState("");
    const [productModel, setProductModel] = useState("");
    const [kit, setKit] = useState("");
    const [soldOutOnly, setSoldOutOnly] = useState(false);
    const [searchIdProduto, setSearchIdProduto] = useState("");
    const [tableMode, setTableMode] = useState("sold");

    const sellersOptions = useMemo(() => {
        return uniqueSorted([
            ...rowsRaw.map((r) => r.seller_nome),
            ...rowsAllRaw.map((r) => r.seller_nome),
        ]);
    }, [rowsRaw, rowsAllRaw]);

    const productModelsOptions = useMemo(() => {
        return uniqueSorted([
            ...rowsRaw.map((r) => r.product_model),
            ...rowsAllRaw.map((r) => r.product_model),
        ]);
    }, [rowsRaw, rowsAllRaw]);

    const fetchData = useCallback(async () => {
        setErrorMsg("");

        if (!startDate || !endDate) {
            setErrorMsg("Selecione as duas datas (inicial e final).");
            return;
        }

        setLoading(true);
        try {
            const payload = { start: toISO(startDate), end: toISO(endDate) };
            const res = await djangoApi.post(
                "products/register_prods/get_sold_prods_query/",
                payload,
                {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const soldData = res?.data?.data_sold || [];
            const allData = res?.data?.data_all || [];

            setRowsRaw(soldData);
            setRowsAllRaw(allData);
        } catch (err) {
            const backendMsg =
                err?.response?.data?.error ||
                err?.response?.data?.msg ||
                err?.message ||
                "Erro inesperado";

            setErrorMsg(backendMsg);
            console.error("Erro ao consultar vendidos:", err);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    const applyLocalFilters = useCallback(
        (baseRows) => {
            let out = [...baseRows];

            if (seller) {
                out = out.filter((r) => r.seller_nome === seller);
            }

            if (productModel) {
                out = out.filter((r) => r.product_model === productModel);
            }

            if (kit) {
                out = out.filter((r) => String(r.kit_number) === String(kit));
            }

            if (soldOutOnly) {
                out = out.filter((r) => r.sold_out === true);
            }

            if (searchIdProduto.trim()) {
                const q = searchIdProduto.trim().toLowerCase();
                out = out.filter((r) =>
                    String(r.product_id_produto || "").toLowerCase().includes(q)
                );
            }

            return out;
        },
        [seller, productModel, kit, soldOutOnly, searchIdProduto]
    );

    const rows = useMemo(() => applyLocalFilters(rowsRaw), [rowsRaw, applyLocalFilters]);
    const rowsAll = useMemo(() => applyLocalFilters(rowsAllRaw), [rowsAllRaw, applyLocalFilters]);

    const kpis = useMemo(() => {
        const totalItens = rows.reduce((acc, r) => acc + Number(r.quantity_sold || 0), 0);
        const totalVendido = rows.reduce((acc, r) => acc + Number(r.subtotal || 0), 0);
        const vendedoresUnicos = new Set(rows.map((r) => r.seller_nome)).size || 0;
        const ticketMedio = totalItens > 0 ? totalVendido / totalItens : 0;

        return { totalItens, totalVendido, vendedoresUnicos, ticketMedio };
    }, [rows]);

    const fmtDateBR = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        return d.toLocaleDateString("pt-BR");
    };

    const soldColumns = [
        { field: "product_id_produto", headerName: "ID Produto", width: 100 },
        { field: "product_model", headerName: "Tipo", width: 120 },
        { field: "product_id_produto_color", headerName: "Cor", width: 120 },
        { field: "seller_nome", headerName: "Vendedora", flex: 1, minWidth: 120, maxWidth: 300 },
        {
            field: "kit_number",
            headerName: "Kit",
            width: 70,
            valueGetter: (p) => String(p.row.kit_number ?? ""),
        },
        { field: "quantity", headerName: "Quantidade", type: "number", width: 110 },
        { field: "quantity_sold", headerName: "Qtd Vendida", type: "number", width: 110 },
        {
            field: "sell_price",
            headerName: "Preço Unit.",
            width: 130,
            valueFormatter: (p) => fmtBRL(p.value),
        },
        {
            field: "subtotal",
            headerName: "Subtotal",
            width: 130,
            valueFormatter: (p) => fmtBRL(p.value),
        },
        {
            field: "kit_returned_at",
            headerName: "Devolvido em",
            width: 130,
            valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleDateString("pt-BR") : "",
        },
        {
            field: "sold_out",
            headerName: "Esgotou?",
            minWidth: 110,
            flex: 1,
            renderCell: (p) =>
                p.value ? (
                    <Chip size="small" color="success" label="Sim" />
                ) : (
                    <Chip size="small" label="Não" />
                ),
            sortable: true,
        },
    ];

    const allColumns = [
        { field: "product_id_produto", headerName: "ID Produto", width: 100 },
        { field: "product_model", headerName: "Tipo", width: 120 },
        { field: "product_id_produto_color", headerName: "Cor", width: 120 },
        { field: "seller_nome", headerName: "Vendedora", flex: 1, minWidth: 120, maxWidth: 300 },
        {
            field: "kit_number",
            headerName: "Kit",
            width: 70,
            valueGetter: (p) => String(p.row.kit_number ?? ""),
        },
        { field: "quantity", headerName: "Quantidade", type: "number", width: 110 },
        { field: "quantity_sold", headerName: "Qtd Vendida", type: "number", width: 110 },
        {
            field: "sell_price",
            headerName: "Preço Unit.",
            width: 130,
            valueFormatter: (p) => fmtBRL(p.value),
        },
        {
            field: "subtotal_all",
            headerName: "Valor Total",
            width: 130,
            valueFormatter: (p) => fmtBRL(p.value),
        },
        {
            field: "kit_returned_at",
            headerName: "Devolvido em",
            width: 130,
            valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleDateString("pt-BR") : "",
        },
        {
            field: "sold_out",
            headerName: "Esgotou?",
            minWidth: 110,
            flex: 1,
            renderCell: (p) =>
                p.value ? (
                    <Chip size="small" color="success" label="Sim" />
                ) : (
                    <Chip size="small" label="Não" />
                ),
            sortable: true,
        },
    ];

    const colorSalesDataSold = useMemo(() => {
        return buildColorSalesData(rows, "quantity_sold", "subtotal");
    }, [rows]);

    const colorSalesDataAll = useMemo(() => {
        return buildColorSalesData(rowsAll, "quantity", "subtotal_all");
    }, [rowsAll]);

    const colorPanel = useMemo(() => {
        const totalVendido = kpis.totalVendido || 0;
        const totalCores = colorSalesDataSold.length;
        const mediaPorCor = totalCores > 0 ? totalVendido / totalCores : 0;

        const topByValue =
            [...colorSalesDataSold].sort((a, b) => b.total - a.total)[0] || {
                color: "—",
                qty: 0,
                total: 0,
            };

        const topByQty =
            [...colorSalesDataSold].sort((a, b) => b.qty - a.qty)[0] || {
                color: "—",
                qty: 0,
                total: 0,
            };

        const topByTicket =
            [...colorSalesDataSold]
                .filter((c) => c.qty > 0)
                .map((c) => ({ ...c, ticket: c.total / c.qty }))
                .sort((a, b) => (b.ticket || 0) - (a.ticket || 0))[0] || {
                color: "—",
                qty: 0,
                total: 0,
                ticket: 0,
            };

        const top5 = [...colorSalesDataSold].slice(0, 8);

        return {
            totalCores,
            mediaPorCor,
            topByValue,
            topByQty,
            topByTicket,
            top5,
            totalVendido,
        };
    }, [colorSalesDataSold, kpis.totalVendido]);

    const handleExportExcel = () => {
        const exportRows = tableMode === "sold" ? rows : rowsAll;

        if (!exportRows.length) {
            window.alert("Não há dados para exportar.");
            return;
        }

        const sheetData =
            tableMode === "sold"
                ? exportRows.map((r) => ({
                    "ID Produto": r.product_id_produto,
                    "Tipo": r.product_model,
                    "Vendedora": r.seller_nome,
                    "Kit": r.kit_number,
                    "Qtd": r.quantity,
                    "Qtd Vendida": r.quantity_sold,
                    "Preço Unit. (R$)": Number(r.sell_price || 0),
                    "Subtotal (R$)": Number(r.subtotal || 0),
                    "Cor": r.product_id_produto_color,
                    "Devolvido em": fmtDateBR(r.kit_returned_at),
                    "Esgotou?": r.sold_out ? "Sim" : "Não",
                }))
                : exportRows.map((r) => ({
                    "ID Produto": r.product_id_produto,
                    "Tipo": r.product_model,
                    "Vendedora": r.seller_nome,
                    "Kit": r.kit_number,
                    "Qtd": r.quantity,
                    "Qtd Vendida": r.quantity_sold,
                    "Preço Unit. (R$)": Number(r.sell_price || 0),
                    "Valor Total (R$)": Number(r.subtotal_all || 0),
                    "Cor": r.product_id_produto_color,
                    "Devolvido em": fmtDateBR(r.kit_returned_at),
                    "Esgotou?": r.sold_out ? "Sim" : "Não",
                }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(sheetData);

        ws["!cols"] = [
            { wch: 14 },
            { wch: 12 },
            { wch: 22 },
            { wch: 6 },
            { wch: 8 },
            { wch: 12 },
            { wch: 16 },
            { wch: 16 },
            { wch: 12 },
            { wch: 14 },
            { wch: 10 },
        ];

        XLSX.utils.book_append_sheet(
            wb,
            ws,
            tableMode === "sold" ? "Vendidos" : "Todos"
        );

        const kpiSheet = XLSX.utils.aoa_to_sheet([
            ["KPI", "Valor"],
            ["Valor Vendido (R$)", Number(kpis.totalVendido || 0)],
            ["Itens Vendidos", Number(kpis.totalItens || 0)],
            ["Ticket Médio (R$)", Number(kpis.ticketMedio || 0)],
            ["Vendedoras Únicas", Number(kpis.vendedoresUnicos || 0)],
            ["Período", `${toISO(startDate)} a ${toISO(endDate)}`],
            ["Modo da Tabela", tableMode === "sold" ? "Somente vendidos" : "Todos os produtos"],
        ]);

        XLSX.utils.book_append_sheet(wb, kpiSheet, "KPIs");

        const filename = `${tableMode === "sold" ? "vendidos" : "todos"}_${toISO(startDate)}_a_${toISO(endDate)}.xlsx`;
        XLSX.writeFile(wb, filename);
    };

    const PIE_COLORS = [
        colors?.greenAccent?.[500] || PIE_COLORS_FALLBACK[0],
        colors?.blueAccent?.[300] || PIE_COLORS_FALLBACK[1],
        colors?.yellow?.[500] || PIE_COLORS_FALLBACK[2],
        "#ffb74d",
        "#29b6f6",
        "#dce775",
        "#7e57c2",
        colors?.grey?.[100] || PIE_COLORS_FALLBACK[7],
        "#66bb6a",
        "#5c6bc0",
    ];

    const piesSold = useMemo(() => buildPieData(colorSalesDataSold), [colorSalesDataSold]);
    const piesAll = useMemo(() => buildPieData(colorSalesDataAll), [colorSalesDataAll]);

    const totalQtySold = useMemo(
        () => piesSold.byQty.reduce((acc, x) => acc + Number(x.value || 0), 0),
        [piesSold.byQty]
    );

    const totalValueSold = useMemo(
        () => piesSold.byVal.reduce((acc, x) => acc + Number(x.value || 0), 0),
        [piesSold.byVal]
    );

    const totalQtyAll = useMemo(
        () => piesAll.byQty.reduce((acc, x) => acc + Number(x.value || 0), 0),
        [piesAll.byQty]
    );

    const totalValueAll = useMemo(
        () => piesAll.byVal.reduce((acc, x) => acc + Number(x.value || 0), 0),
        [piesAll.byVal]
    );

    const tableRows = tableMode === "sold" ? rows : rowsAll;
    const tableColumns = tableMode === "sold" ? soldColumns : allColumns;

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <Box p={2} mt={5}>
                <Typography variant="h5" fontWeight={800} gutterBottom>
                    Análise de Produtos Vendidos
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{ p: 2, mb: 2, bgcolor: colors.pink[700], borderRadius: 2 }}
                    elevation={8}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={1.5}>
                            <DatePicker
                                label="Data Inicial"
                                value={startDate}
                                onChange={setStartDate}
                                slotProps={{ textField: { fullWidth: true, required: true } }}
                            />
                        </Grid>

                        <Grid item xs={12} md={1.5}>
                            <DatePicker
                                label="Data Final"
                                value={endDate}
                                onChange={setEndDate}
                                slotProps={{ textField: { fullWidth: true, required: true } }}
                            />
                        </Grid>

                        <Grid item xs={12} md={2.1}>
                            <FormControl fullWidth>
                                <InputLabel>Vendedora</InputLabel>
                                <Select
                                    label="Vendedora"
                                    value={seller}
                                    size="small"
                                    onChange={(e) => setSeller(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>(Todas)</em>
                                    </MenuItem>
                                    {sellersOptions.map((s) => (
                                        <MenuItem key={s} value={s}>
                                            {s}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>Tipo</InputLabel>
                                <Select
                                    label="Tipo"
                                    value={productModel}
                                    size="small"
                                    onChange={(e) => setProductModel(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>(Todos)</em>
                                    </MenuItem>
                                    {productModelsOptions.map((pm) => (
                                        <MenuItem key={pm} value={pm}>
                                            {pm}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="Buscar por ID do Produto"
                                value={searchIdProduto}
                                onChange={(e) => setSearchIdProduto(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Stack direction="row" spacing={1} justifyContent="flex-start">
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => {
                                        setSeller("");
                                        setProductModel("");
                                        setKit("");
                                        setSoldOutOnly(false);
                                        setSearchIdProduto("");
                                    }}
                                >
                                    Limpar filtros
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={fetchData}
                                    color="success"
                                    disabled={!startDate || !endDate || loading}
                                >
                                    {loading ? "Consultando..." : "Buscar"}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} alignItems="center" mt={1}>
                        <Grid item xs={12} md={0.5}>
                            <Stack direction="row" spacing={1}>
                                <Tooltip title="Exportar Excel">
                                    <span>
                                        <IconButton
                                            color="success"
                                            onClick={handleExportExcel}
                                            disabled={!tableRows.length}
                                        >
                                            <FontAwesomeIcon
                                                icon={faFileExcel}
                                                size="lg"
                                                style={{ color: colors.greenAccent[400] }}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        color="success"
                                        checked={soldOutOnly}
                                        onChange={(e) => setSoldOutOnly(e.target.checked)}
                                    />
                                }
                                label="Somente esgotados"
                            />
                        </Grid>
                    </Grid>

                    {errorMsg && (
                        <Typography color="error" mt={1}>
                            {errorMsg}
                        </Typography>
                    )}
                </Paper>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 2, bgcolor: colors.pink[700], borderRadius: 2 }}
                            elevation={8}
                        >
                            <Typography variant="overline">Valor Vendido</Typography>
                            <Typography variant="h6" fontWeight={800}>
                                {fmtBRL(kpis.totalVendido)}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 2, bgcolor: colors.pink[700], borderRadius: 2 }}
                            elevation={8}
                        >
                            <Typography variant="overline">Itens Vendidos</Typography>
                            <Typography variant="h6" fontWeight={800}>
                                {kpis.totalItens}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 2, bgcolor: colors.pink[700], borderRadius: 2 }}
                            elevation={8}
                        >
                            <Typography variant="overline">Ticket Médio</Typography>
                            <Typography variant="h6" fontWeight={800}>
                                {fmtBRL(kpis.ticketMedio)}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 2, bgcolor: colors.pink[700], borderRadius: 2 }}
                            elevation={8}
                        >
                            <Typography variant="overline">Vendedoras</Typography>
                            <Typography variant="h6" fontWeight={800}>
                                {kpis.vendedoresUnicos}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {rowsRaw?.length > 0 && (
                    <Grid container spacing={2} mb={2} sx={{ minHeight: "760px" }}>
                        <Grid item xs={12} md={4} sx={{ display: "flex", flexDirection: "column" }}>
                            <Grid
                                container
                                direction="column"
                                sx={{ height: 720, display: "flex", flex: 1, gap: 2, minHeight: 0 }}
                                wrap="nowrap"
                            >
                                <Grid item sx={{ flex: 1, minHeight: 0 }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            bgcolor: colors.pink[700],
                                            height: "100%",
                                            minHeight: 0,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2,
                                            borderRadius: 2,
                                        }}
                                        elevation={8}
                                    >
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Resumo por Cor — Vendidos
                                        </Typography>

                                        <Divider />

                                        <Box>
                                            <List dense disablePadding>
                                                {colorPanel.top5.map((c) => {
                                                    const share =
                                                        colorPanel.totalVendido > 0
                                                            ? (c.total / colorPanel.totalVendido) * 100
                                                            : 0;

                                                    return (
                                                        <ListItem
                                                            key={c.color}
                                                            sx={{
                                                                px: 0,
                                                                py: 0.75,
                                                                alignItems: "flex-start",
                                                                flexDirection: "column",
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: "100%",
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2" fontWeight={700}>
                                                                            {c.color}
                                                                        </Typography>
                                                                    }
                                                                    secondary={
                                                                        <Typography variant="caption">
                                                                            Qtd: {c.qty}
                                                                        </Typography>
                                                                    }
                                                                    sx={{ m: 0 }}
                                                                />
                                                                <Typography variant="body2" fontWeight={700}>
                                                                    {fmtBRL(c.total)}
                                                                </Typography>
                                                            </Box>

                                                            <LinearProgress
                                                                variant="determinate"
                                                                color="success"
                                                                value={Math.min(100, share)}
                                                                sx={{ width: "100%", height: 6, borderRadius: 1 }}
                                                            />
                                                        </ListItem>
                                                    );
                                                })}

                                                {colorPanel.top5.length === 0 && (
                                                    <Typography variant="body2">
                                                        Sem dados no período.
                                                    </Typography>
                                                )}
                                            </List>
                                        </Box>

                                        <Divider />

                                        <Box sx={{ mt: "auto" }}>
                                            <Typography variant="overline" display="block" mb={1}>
                                                Destaques
                                            </Typography>

                                            <Stack spacing={1}>
                                                <HighlightCard
                                                    title="Maior Valor"
                                                    value={`${colorPanel.topByValue.color} — ${fmtBRL(colorPanel.topByValue.total)}`}
                                                    colors={colors}
                                                    bg={colors.greenAccent[700]}
                                                />
                                                <HighlightCard
                                                    title="Maior Quantidade"
                                                    value={`${colorPanel.topByQty.color} — ${colorPanel.topByQty.qty} itens`}
                                                    colors={colors}
                                                    bg={colors.greenAccent[700]}
                                                />
                                                <HighlightCard
                                                    title="Melhor Ticket Médio"
                                                    value={`${colorPanel.topByTicket.color} — ${fmtBRL(colorPanel.topByTicket.ticket || 0)}`}
                                                    colors={colors}
                                                    bg={colors.greenAccent[700]}
                                                />
                                                <HighlightCard
                                                    title="Média por Cor"
                                                    value={fmtBRL(colorPanel.mediaPorCor || 0)}
                                                    colors={colors}
                                                    bg={colors.blueAccent[700]}
                                                />
                                            </Stack>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column" }}>
                            <Grid container spacing={2} sx={{ height: 720, flex: 1, minHeight: 0 }}>
                                <Grid item xs={12} md={6} sx={{ minHeight: 350 }}>
                                    <ColorPieCard
                                        title="Quantidade por Cor — Vendidos"
                                        data={piesSold.byQty}
                                        total={totalQtySold}
                                        pieColors={PIE_COLORS}
                                        colors={colors}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6} sx={{ minHeight: 350 }}>
                                    <ColorPieCard
                                        title="Quantidade por Cor — Todos os Produtos"
                                        data={piesAll.byQty}
                                        total={totalQtyAll}
                                        pieColors={PIE_COLORS}
                                        colors={colors}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6} sx={{ minHeight: 350 }}>
                                    <ColorPieCard
                                        title="Valor por Cor — Vendidos"
                                        data={piesSold.byVal}
                                        total={totalValueSold}
                                        pieColors={PIE_COLORS}
                                        colors={colors}
                                        isCurrency
                                    />
                                </Grid>

                                <Grid item xs={12} md={6} sx={{ minHeight: 350 }}>
                                    <ColorPieCard
                                        title="Valor por Cor — Todos os Produtos"
                                        data={piesAll.byVal}
                                        total={totalValueAll}
                                        pieColors={PIE_COLORS}
                                        colors={colors}
                                        isCurrency
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}

                <Paper variant="outlined" sx={{ bgcolor: colors.pink[700], borderRadius: 2, overflow: "hidden" }}>
                    <Box
                        sx={{
                            px: 2,
                            pt: 2,
                            pb: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={700}>
                            Tabela de Produtos
                        </Typography>

                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={tableMode}
                            onChange={(_, value) => {
                                if (value) setTableMode(value);
                            }}
                        >
                            <ToggleButton value="sold">Somente vendidos</ToggleButton>
                            <ToggleButton value="all">Todos os produtos</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box sx={{ height: 560 }}>
                        <DataGrid
                            rows={tableRows}
                            columns={tableColumns}
                            getRowId={(r, index) =>
                                r.id ?? `${r.product_id_produto || "prod"}-${r.kit_number || "kit"}-${index}`
                            }
                            loading={loading}
                            disableRowSelectionOnClick
                            pageSizeOptions={[10, 25, 50, 100]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25, page: 0 } },
                                sorting: { sortModel: [{ field: "kit_returned_at", sort: "asc" }] },
                            }}
                        />
                    </Box>
                </Paper>
            </Box>
        </LocalizationProvider>
    );
}